import React, { createContext, useContext, useState, ReactNode } from "react";
import { useMediaQuery, Theme } from "@mui/material";

// Define the context type
interface ColumnVisibilityContextType {
  leftColumnVisible: boolean;
  middleColumnVisible: boolean;
  rightColumnVisible: boolean;
  showLeftColumn: () => void;
  showMiddleColumn: () => void;
  showRightColumn: () => void;
  hideRightColumn: () => void;
}

// Create context with undefined to enforce use within provider
const ColumnVisibilityContext = createContext<ColumnVisibilityContextType | undefined>(undefined);

// Define props for the provider
interface ColumnVisibilityProviderProps {
  children: ReactNode;
}

export const ColumnVisibilityProvider: React.FC<ColumnVisibilityProviderProps> = ({ children }) => {
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const [leftColumnVisible, setLeftColumnVisible] = useState(true);
  const [middleColumnVisible, setMiddleColumnVisible] = useState(!mobile);
  const [rightColumnVisible, setRightColumnVisible] = useState(false);

  const showLeftColumn = () => {
    setLeftColumnVisible(true);
    if (mobile) setMiddleColumnVisible(false);
    setRightColumnVisible(false);
  };

  const showMiddleColumn = () => {
    if (mobile) setLeftColumnVisible(false);
    setMiddleColumnVisible(true);
    setRightColumnVisible(false);
  };

  const showRightColumn = () => {
    if (mobile) {
      setLeftColumnVisible(false);
      setMiddleColumnVisible(false);
    }
    setRightColumnVisible(true);
  };

  const hideRightColumn = () => {
    if (mobile) setLeftColumnVisible(false);
    setMiddleColumnVisible(true);
    setRightColumnVisible(false);
  };

  return (
    <ColumnVisibilityContext.Provider
      value={{
        leftColumnVisible,
        middleColumnVisible,
        rightColumnVisible,
        showLeftColumn,
        showMiddleColumn,
        showRightColumn,
        hideRightColumn,
      }}
    >
      {children}
    </ColumnVisibilityContext.Provider>
  );
};

// Hook for consuming the context
export const useColumnVisibilityContext = (): ColumnVisibilityContextType => {
  const context = useContext(ColumnVisibilityContext);
  if (!context)
    throw new Error("useColumnVisibilityContext must be used within a ColumnVisibilityProvider");
  return context;
};
